import { useCookies } from "react-cookie";
import {
  RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
} from "config/cookies";
import { extractDomain, getExpDateByDays } from "utils/cookiesUtils";
import { useDisclosure } from "@chakra-ui/react";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { useEffect } from "react";
import config from "config/config";
import type { LanguageCode } from "config/languages";
import { useLanguage } from "contexts/LocalizationContext";

const RETURNING_USERS_VIEW_EVENT_UA = ["viewed", "ReturningUsers", "Notification"] as const;
const RETURNING_USERS_VIEW_EVENT = { event: "returning_users", label: "viewed", placement: "notification" } as const;

const getFunnelUrl = (postEligibilityCookie: string | undefined, language: LanguageCode) => {
  if (postEligibilityCookie) return `${config.funnelUrl}/claim_enquiries/${postEligibilityCookie}?lang=${language}`;
  return null;
};

const useReturningUsersNotification = () => {
  const language = useLanguage();

  const [cookies, setCookie] = useCookies([
    RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
  ]);

  const funnelUrl = getFunnelUrl(cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME], language);

  const hasFunnelCookie =
    cookies[RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME] || cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME];
  const notificationClosedCookie = cookies[RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME];
  const shouldBeOpen = funnelUrl && hasFunnelCookie && !notificationClosedCookie;

  const { isOpen, onClose: onDisclosureClose, onOpen: onDisclosureOpen } = useDisclosure({ defaultIsOpen: false });

  const onClose = () => {
    onDisclosureClose();
    setCookie(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME, "true", {
      domain: extractDomain(),
      path: "/",
      expires: getExpDateByDays(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION),
    });
  };

  useEffect(() => {
    onDisclosureClose();
    if (shouldBeOpen) {
      onDisclosureOpen();
      trackUAEvent(RETURNING_USERS_VIEW_EVENT_UA);
      trackCustomEvent(RETURNING_USERS_VIEW_EVENT);
    }
  }, [shouldBeOpen, onDisclosureOpen, onDisclosureClose]);

  return { isOpen, onClose, funnelUrl };
};

export default useReturningUsersNotification;
